import React from 'react';
import PropTypes from 'prop-types';
import { getMenuConfig } from './MenuConfig';

const UnifiedFooter = ({ mainUrl, docsUrl, logo }) => {
  const MENU_CONFIG = getMenuConfig({ mainUrl, docsUrl });

  const LINK_CLASSES = 'text-gray-600 text-[15px] hover:text-gray-400 duration-100';
  const COLUMN_TITLE_CLASSES = 'text-[16px] font-semibold mb-2';
  const LOCATION_ICON_CLASSES = 'size-5 text-lg text-gray-600 mb-[1px]';
  const SOCIAL_ICON_CLASSES = 'size-6 aspect-square hover:text-gray-600 duration-200';

  const locations = [{ name: 'San Francisco, CA' }, { name: 'London, UK' }];

  const socialLinks = [
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/sophare-ai/',
      viewBox: '0 0 448 512',
      path: 'M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z',
    },
    {
      name: 'Twitter',
      href: 'https://x.com/sophareai',
      viewBox: '0 0 24 24',
      path: 'M10.4883 14.651L15.25 21H22.25L14.3917 10.5223L20.9308 3H18.2808L13.1643 8.88578L8.75 3H1.75L9.26086 13.0145L2.31915 21H4.96917L10.4883 14.651ZM16.25 19L5.75 5H7.75L18.25 19H16.25Z',
    },
  ];

  const LocationPinIcon = () => (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      version="1.2"
      baseProfile="tiny"
      viewBox="0 0 24 24"
      className={LOCATION_ICON_CLASSES}
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M10.368 19.102c.349 1.049 1.011 1.086 1.478.086l5.309-11.375c.467-1.002.034-1.434-.967-.967l-11.376 5.308c-1.001.467-.963 1.129.085 1.479l4.103 1.367 1.368 4.102z"></path>
    </svg>
  );

  const SocialIcon = ({ href, viewBox, path, name }) => (
    <a href={href} aria-label={name}>
      <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox={viewBox}
        className={SOCIAL_ICON_CLASSES}
        height="1em"
        width="1em"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={path}></path>
      </svg>
    </a>
  );

  SocialIcon.propTypes = {
    href: PropTypes.string.isRequired,
    viewBox: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  };

  const FooterLink = ({ href, children, className = LINK_CLASSES }) => (
    <a className={className} href={href}>
      {children}
    </a>
  );

  FooterLink.propTypes = {
    href: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
  };

  const LocationItem = ({ name }) => (
    <div className="text-sm mt-1 lg:mt-1.5 flex flex-row items-center -ml-0.5 pt-0.5">
      <LocationPinIcon />
      <div className="text-gray-600 text-[15px]">{name}</div>
    </div>
  );

  LocationItem.propTypes = {
    name: PropTypes.string.isRequired,
  };

  const directoryColumns = [
    { key: 'solutions', title: MENU_CONFIG.solutions.label, sections: MENU_CONFIG.solutions.sections },
    { key: 'resources', title: MENU_CONFIG.resources.label, sections: MENU_CONFIG.resources.sections },
    {
      key: 'company',
      title: MENU_CONFIG.company.label,
      sections: [
        { key: 'company-items', items: MENU_CONFIG.company.items },
        { key: 'pricing', title: null, items: MENU_CONFIG.navigation },
        { key: 'support', title: 'Support', items: [{ label: 'team@sophare.ai', href: 'mailto:team@sophare.ai' }] },
      ],
    },
  ];

  return (
    <div className="footer bg-white border-t border-gray-100 w-screen" id="footer">
      <div className="bg-white h-fit py-8 sm:py-20 md:py-[100px] content w-screen">
        <div className="sm:hidden">
          <div className="flex flex-col justify-between">
            <div>
              <div className="font-bold text-lg">Sophare AI</div>
              {locations.map((location, index) => (
                <LocationItem key={location.name} name={location.name} className={index === 1 ? 'pb-10' : ''} />
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-black max-lg:mt-6">
          <div className="hidden sm:flex">
            <div className="flex flex-col justify-between">
              <div>
                <div className="flex items-center">
                  {logo}
                  <div className="font-bold text-lg">Sophare AI</div>
                </div>
                {locations.map((location) => (
                  <LocationItem key={location.name} name={location.name} />
                ))}
              </div>
              <div>
                <div className="flex flex-row gap-2 text-gray-400 max-lg:mt-6">
                  {socialLinks.map((social) => (
                    <SocialIcon
                      key={social.name}
                      href={social.href}
                      viewBox={social.viewBox}
                      path={social.path}
                      name={social.name}
                    />
                  ))}
                </div>
                <div className="text-sm text-gray-400 mt-2">© 2026 Sophare</div>
              </div>
            </div>
          </div>
          {directoryColumns.map((column) => (
            <div key={column.key} className="flex flex-col gap-4">
              <div className={COLUMN_TITLE_CLASSES}>{column.title}</div>
              {column.key === 'resources' ? (
                <div className="flex flex-col gap-1">
                  {column.sections.map((section) => {
                    if (!section.title || section.titleHidden) {
                      return null;
                    }
                    const sectionHref = section.titleHref || section.items?.[0]?.href;
                    if (!sectionHref) {
                      return null;
                    }
                    return (
                      <FooterLink key={section.key || section.title} href={sectionHref}>
                        {section.title}
                      </FooterLink>
                    );
                  })}
                </div>
              ) : (
                column.sections.map((section) => (
                  <div key={section.key || section.title}>
                    {section.title ? <div className="font-medium text-[15px] mt-0.5 mb-1">{section.title}</div> : null}
                    <div className="flex flex-col gap-1">
                      {section.items.map((item) => (
                        <FooterLink key={item.label} href={item.href}>
                          {item.label}
                        </FooterLink>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
        <div className="sm:hidden mt-6">
          <div className="flex flex-row gap-5 text-gray-400">
            {socialLinks.map((social) => (
              <SocialIcon
                key={social.name}
                href={social.href}
                viewBox={social.viewBox}
                path={social.path}
                name={social.name}
              />
            ))}
          </div>
          <div className="text-sm text-gray-400 mt-2">© 2026 Sophare</div>
        </div>
      </div>
    </div>
  );
};

UnifiedFooter.propTypes = {
  mainUrl: PropTypes.string.isRequired,
  docsUrl: PropTypes.string.isRequired,
  logo: PropTypes.node.isRequired,
};

export default UnifiedFooter;
